import type { BlobArrayLike } from "@pdfking/compatibility"
import type { PdfiumApi, PdfiumTypeMap, Pointer } from "@pdfking/pdfium-wrap"
import type { PDFReaderOptions } from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"
import { BlobArray, BlobToUint8 } from "@pdfking/compatibility"
import { PdfiumExports as E } from "@pdfking/pdfium-wrap"
import { Constant, IterableToArray, MapAsync } from "@pdfking/tfunc"

type PointerAvail = PdfiumTypeMap["FPDF_AVAIL"]
type PointerDoc = PdfiumTypeMap["FPDF_DOCUMENT"]

const CHUNK_SIZE = (1024 * 1024 * 30) as Bytes

const PDF_DATA_NOTAVAIL = 0
const PDF_DATA_AVAIL = 1

const is_data_available = (blob_size: number, offset: number, size: number) =>
  offset + size <= blob_size

const get_data_from_blob = (
  blob: BlobArrayLike,
  offset: number,
  size: number
) => {
  const sliced = blob.slice(offset, offset + size)
  return new Uint8Array(sliced.arrayBufferSync())
}

export type ReadDocumentResult = {
  avail: null | PointerAvail
  blob_array: BlobArrayLike
  cleanup_functions: Array<() => void>
  doc_pointer: PointerDoc
  file_pointer: null | number
  hints_ptr: null | number
  promise_generator: AsyncGenerator<void> | null
  size: number
}

export const ReadDocument = async (
  api: PdfiumApi,
  options: PDFReaderOptions
): Promise<ReadDocumentResult> => {
  const {
    addCallback,
    call,
    malloc,
    setInt32,
    writeBytes,
    writeString,
    writeTo,
  } = api
  const { password = "", size, stream } = options

  const { free: freePassword, pointer: passwordPtr } = writeString(password)

  const cleanup_functions: Array<() => void> = [freePassword]

  const blob_array = BlobArray()

  if (size <= CHUNK_SIZE) {
    const uint8Array = await IterableToArray(stream)
      .then(BlobArray)
      .then(BlobToUint8)

    const { free: freeData, pointer: dataPtr } = writeBytes(uint8Array)
    cleanup_functions.push(freeData)

    const doc_pointer = await call(
      E.FPDF_LoadMemDocument,
      dataPtr,
      uint8Array.length,
      passwordPtr
    )

    return {
      avail: null,
      blob_array,
      cleanup_functions,
      doc_pointer,
      file_pointer: null,
      hints_ptr: null,
      promise_generator: null,
      size,
    }
  }

  const promise_generator = MapAsync(chunk => blob_array.push(chunk), stream)

  const isDataAvail = (_pThis: number, offset: number, size: number) =>
    is_data_available(blob_array.size, offset, size) ? 1 : 0

  const { free: freeIsDataAvail, pointer: isDataAvailPtr } = addCallback(
    isDataAvail,
    "iiii"
  )
  cleanup_functions.push(freeIsDataAvail)

  const { free: freeFileAvail, pointer: fileAvailPtr } = malloc(8 as Bytes)
  cleanup_functions.push(freeFileAvail)

  setInt32(fileAvailPtr, 1)
  setInt32((fileAvailPtr + 4) as Pointer, isDataAvailPtr)

  const addSegment = Constant(0)

  const { free: freeAddSegment, pointer: addSegmentPtr } = addCallback(
    addSegment,
    "viii"
  )
  cleanup_functions.push(freeAddSegment)

  const { free: freeHints, pointer: hints_ptr } = malloc(8 as Bytes)
  cleanup_functions.push(freeHints)

  setInt32(hints_ptr, 1)
  setInt32((hints_ptr + 4) as Pointer, addSegmentPtr)

  const getBlock = (
    _param: number,
    position: number,
    pBuf: number,
    size: number
  ) => {
    if (!is_data_available(blob_array.size, position, size)) {
      return 0
    }

    const data = get_data_from_blob(blob_array, position, size)
    writeTo(pBuf as Pointer, data)
    return 1
  }

  const { free: freeGetBlock, pointer: getBlockPtr } = addCallback(
    getBlock,
    "iiiii"
  )
  cleanup_functions.push(freeGetBlock)

  const { free: freeFileAccess, pointer: file_pointer } = malloc(12 as Bytes)
  cleanup_functions.push(freeFileAccess)

  setInt32(file_pointer, size)
  setInt32((file_pointer + 4) as Pointer, getBlockPtr)
  setInt32((file_pointer + 8) as Pointer, 0)

  const avail = await call(E.FPDFAvail_Create, fileAvailPtr, file_pointer)
  cleanup_functions.push(() => void call(E.FPDFAvail_Destroy, avail))

  let doc_pointer: null | PointerDoc = null
  let avail_done = false

  while (!avail_done) {
    const doc_status = await call(E.FPDFAvail_IsDocAvail, avail, hints_ptr)

    if (doc_status === PDF_DATA_AVAIL) {
      avail_done = true
    } else if (doc_status === PDF_DATA_NOTAVAIL) {
      const { done } = await promise_generator.next()
      avail_done = done ?? false
    } else {
      throw new Error(`Document loading error: status ${doc_status} source`)
    }
  }

  doc_pointer = await call(E.FPDFAvail_GetDocument, avail, passwordPtr)

  return {
    avail,
    blob_array,
    cleanup_functions,
    doc_pointer,
    file_pointer,
    hints_ptr,
    promise_generator,
    size,
  }
}
